import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";

import { getTutorials } from "../../../store/tutorials";
import { getReportDetails, setLastPhotoNumber } from "../../../store/reportDetails";
import { getCoverData } from "../../../store/coverData";
import { getCurrentUser } from "../../../services/authService";
import { DEFAULT_REPORT_TITLE } from "../../../utils/constants";
import { PDFImageProps, User } from "../../../utils/models";
import { tutorialConstants } from '../../../utils/constants';
import { reArrangePhotoRecord, validateWorkOrderNumber } from "../../../utils/utilities";
import { useAppDispatch, useAppSelector } from "../../../store/configureStore";
import { PIPING_LOCATION_OPTIONS } from "../custom-visual-inspection/existing-piping-item";
import { EQUIPMENT_LOCATION_OPTIONS } from "../custom-visual-inspection/existing-equipment-item";
import { getEquipmentAnomalyClassOptions, getPipingAnomalyClassOptions, getStructureAnomalyClassOptions } from "../../../utils/data";

import Modal from "../../themed/modal";
import Button from "../../themed/button";
import Elevation from "../../themed/instruction-modals/elevation";
import LoadingBadge from "../../themed/loading-badge";
import CustomLRUTReport from ".";
import Conditional from "../../common/conditional";
import CustomCoverForm from '../../forms/custom-fvi/coverForm';
import FVICoverData from "../../themed/fvi-cover-data";
import PDOCheckList, { PDOChecklistItem } from "./pdoCheckList";
import ReportTitleForm from "../../forms/custom-fvi/reportTitleForm";
import SyncTime from "../../blocks/sync-time";
import Zippy from "../../themed/zippy";
import WarningNotice from "../custom-visual-inspection/warning-notice";
import VIPhotoRecordForm from "../../forms/visual-inspection/viPhotoRecordForm";

import useAutoSaveReport from "../../../hooks/useAutoSave";
import useProjectDetails from "../../../hooks/useProjectDetails";

import config from "../../../utils/config";
import toastService from "../../../services/toastService";

const CreatePDOReport: React.FC = () => {
    const { projectDetails, error } = useProjectDetails();
	const { activeTutorial } = useAppSelector(getTutorials);
	const { isSaving } = useAppSelector(getReportDetails);
	const { fviCoverData } = useAppSelector(getCoverData);

	const [previewReport, setPreviewReport] = useState(false);
	const [coverData, setCoverData] = useState(fviCoverData);
	const [reportTitle, setReportTitle] = useState(DEFAULT_REPORT_TITLE);
    const [generalChecklistItems, setGeneralChecklistItems] = useState<PDOChecklistItem[]>(generalChecklist);
    const [vesselChecklistItems, setVesselChecklistItems] = useState<PDOChecklistItem[]>(vesselChecklist);
    const [pipingChecklistItems, setPipingChecklistItems] = useState<PDOChecklistItem[]>(pipingChecklist);
    const [structuresChecklistItems, setStructuresChecklistItems] = useState<PDOChecklistItem[]>(structuresChecklist);
    const [generalPhotos, setGeneralPhotos] = useState<PDFImageProps[]>([]);

    const reportDetails = useMemo(() => {
        return {
            coverData,
            reportTitle,
            projectDetails,
            generalPhotos,
            generalChecklistItems,
            vesselChecklistItems,
            pipingChecklistItems,
            structuresChecklistItems,
        }
    }, [coverData, reportTitle, projectDetails, generalPhotos, generalChecklistItems, vesselChecklistItems, pipingChecklistItems, structuresChecklistItems]);

    const validationMessage = useMemo(() => {
        if (!validateWorkOrderNumber(reportDetails.coverData.workOrderNumber)) {
            return 'Enter a valid work order number!';
        }

        return ''
    }, [reportDetails]);

    const autoSave = useAutoSaveReport(reportDetails);
	const dispatch = useAppDispatch();
    const user = getCurrentUser() as User;

	const togglePreviewReport = useCallback((): void => {
		if (config.enableReportCompletionBeforePublish) {
			if (validationMessage) return toastService.error(validationMessage);
		}

		setPreviewReport((p: boolean) => !p);
	}, [validationMessage]);

    const handleSaveAndPreview = useCallback((): void => {
		autoSave.saveReport();
		togglePreviewReport();
	}, [autoSave, togglePreviewReport]);

    useEffect(() => {
        function initializeCache() {
            const cachedReport = autoSave.oldReportData as typeof reportDetails;
            if (!cachedReport) {
                if (fviCoverData.coverData.length > 0) setCoverData(fviCoverData);

                return;
            }

            setCoverData(cachedReport.coverData);
            setReportTitle(cachedReport.reportTitle);
            setGeneralChecklistItems(cachedReport.generalChecklistItems);
            setVesselChecklistItems(cachedReport.vesselChecklistItems);
            setPipingChecklistItems(cachedReport.pipingChecklistItems);
            setStructuresChecklistItems(cachedReport.structuresChecklistItems);

            const reArrangedRecords = reArrangePhotoRecord(cachedReport.generalPhotos);
            setGeneralPhotos(reArrangedRecords);
            dispatch(setLastPhotoNumber(reArrangedRecords.length));
        }

        initializeCache();
    }, [autoSave.oldReportData, dispatch, fviCoverData]);

    return ( 
        <Fragment>
            <Modal isVisible={previewReport} onClose={togglePreviewReport}>
                <CustomLRUTReport {...reportDetails} />
            </Modal>

            <div className="mb-4">
                <SyncTime lastSynced={autoSave.lastSaved} />
            </div>

            {projectDetails && (
                <FVICoverData
                    coverDataId={config.coverDataTemplates.lrut}
                    isVisible={projectDetails.inspectionTypeId === config.supportedTemplates.pdo && user.role.approveReports}
                    subtitle={`Update the Cover Data information. This is the default data used on all ${projectDetails.organisationName} ${projectDetails.inspectionTypeName} Reports`}
                />
            )}

            <Conditional isVisible={validationMessage.length > 0}>
                <WarningNotice title='Incomplete Report' body={validationMessage} />
            </Conditional>

			<Zippy headerTitle="Report Title" isValid={reportTitle.length > 0}>
				<ReportTitleForm projectTitle={projectDetails?.projectTitle} onAdd={setReportTitle} />
			</Zippy>

			<Zippy headerTitle="Cover Sheet" isValid={coverData.coverData.length > 0}>
				<CustomCoverForm initialData={coverData} onAddCoverForm={setCoverData} />
			</Zippy>
			
            <PDOCheckList
                locations={PIPING_LOCATION_OPTIONS}
                damageClasses={getPipingAnomalyClassOptions()}
                title="General Checklist"
                checklistItems={generalChecklistItems}
                onChecklistItemsChange={setGeneralChecklistItems}
            />

            <PDOCheckList
                locations={EQUIPMENT_LOCATION_OPTIONS}
                damageClasses={getEquipmentAnomalyClassOptions()}
                title="Vessel & Equipment"
                checklistItems={vesselChecklistItems}
                onChecklistItemsChange={setVesselChecklistItems}
            />
            
            <PDOCheckList
                locations={PIPING_LOCATION_OPTIONS}
                damageClasses={getPipingAnomalyClassOptions()}
                title="Piping"
                checklistItems={pipingChecklistItems}
                onChecklistItemsChange={setPipingChecklistItems}
            />
            
            <PDOCheckList
                locations={PIPING_LOCATION_OPTIONS}
                damageClasses={getStructureAnomalyClassOptions()}
                title="Structures"
                checklistItems={structuresChecklistItems}
                onChecklistItemsChange={setStructuresChecklistItems}
            />

            <Zippy headerTitle="General Photos" isValid={generalPhotos.length > 0}>
                <VIPhotoRecordForm
                    initialGeneralPhotos={generalPhotos}
                    onProceed={setGeneralPhotos}
                />
			</Zippy>

			<div className="d-flex justify-content-between align-items-center">
				<div>
					<Conditional isVisible={autoSave.isProcessing}>
						<LoadingBadge message="Saving" />
					</Conditional>
				</div>

				<Elevation isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}>
					<Button
						label="Save and Preview Report"
						disabled={error || autoSave.isProcessing || isSaving }
						onClick={handleSaveAndPreview}
					/>
				</Elevation>
			</div>
        </Fragment>
    );
};

const generalChecklist: PDOChecklistItem[] = [
    {
        label: 'Visually inspect the work area for any pre-existing dropped object hazards, such as loose items and debris.',
        hasAnomalies: '',
        anomalyItems: [],
        generalPhotos: [],
    },
    {
        label: 'Are all tools fastened and adequately secured',
        hasAnomalies: '',
        anomalyItems: [],
        generalPhotos: [],
    },
    {
        label: 'Secure/Tie-down of equipment/objects and tools in windy conditions or inclement weather',
        hasAnomalies: '',
        anomalyItems: [],
        generalPhotos: [],
    },
    {
        label: 'Are there any visible signs of floor gaps, grits, mesh damages and handrails',
        hasAnomalies: '',
        anomalyItems: [],
        generalPhotos: [],
    },
    {
        label: 'Tools and tackles to be adequately secured  while working on height.',
        hasAnomalies: '',
        anomalyItems: [],
        generalPhotos: [],
    },
    {
        label: 'Be aware of moving machinery, cranes, and vehicles. Identify all paths of travel and potential for collisions or load shifts.',
        hasAnomalies: '',
        anomalyItems: [],
        generalPhotos: [],
    },
    {
        label: 'After completion of activity proper housekeeping shall be done , as it may contribute to potential dropped objects (PDO).',
        hasAnomalies: '',
        anomalyItems: [],
        generalPhotos: [],
    },
];

const vesselChecklist: PDOChecklistItem[] = [
    {
        label: 'Are vessel supports in good condition? (Clamps,Cleats, foundation bolts)',
        hasAnomalies: '',
        anomalyItems: [],
        generalPhotos: [],
    },
    {
        label: 'Are connections to gauge levels, instrumentations, pressure relief valves, and other related equipment intact?',
        hasAnomalies: '',
        anomalyItems: [],
        generalPhotos: [],
    },
    {
        label: 'Do you see any signs of corrosion on supporting structures and piping to the Ancillary Equipment?',
        hasAnomalies: '',
        anomalyItems: [],
        generalPhotos: [],
    },
    {
        label: 'For insulated vessels, check for condition of cleats and structural components protruding outside the insulation',
        hasAnomalies: '',
        anomalyItems: [],
        generalPhotos: [],
    },
    {
        label: 'Is Integrity of  SKIRT, SADDLES etc intact?',
        hasAnomalies: '',
        anomalyItems: [],
        generalPhotos: [],
    },
    {
        label: 'Check for damaged insulation cladding sheet',
        hasAnomalies: '',
        anomalyItems: [],
        generalPhotos: [],
    },
    {
        label: 'For heavily vibrating services/structures; check for bolt tighness',
        hasAnomalies: '',
        anomalyItems: [],
        generalPhotos: [],
    },
    {
        label: 'Check for Integrity of Name-Plate.',
        hasAnomalies: '',
        anomalyItems: [],
        generalPhotos: [],
    },
];

const pipingChecklist: PDOChecklistItem[] = [
    {
        label: 'Check for integrity of piping hanger (Instances of cracked or broken hangers, “bottoming out” of spring supports, support shoes displaced from support members, or other improper restraint conditions)',
        hasAnomalies: '',
        anomalyItems: [],
        generalPhotos: [],
    },
    {
        label: 'Check for Small bore connections which are not properly supported and having excessive vibrations or mass attached at the free end.',
        hasAnomalies: '',
        anomalyItems: [],
        generalPhotos: [],
    },
    {
        label: 'Check for improper flanges, damage on temporary repairs (clamps), modifications (flexible hoses), or valves of improper specification',
        hasAnomalies: '',
        anomalyItems: [],
        generalPhotos: [],
    },
    {
        label: 'Dummy leg /pipe supports can be checked for excessive corrosion and displacement',
        hasAnomalies: '',
        anomalyItems: [],
        generalPhotos: [],
    },
];

const structuresChecklist: PDOChecklistItem[] = [
    {
        label: 'Check for corrosion on structural members like beams, colums, ties (especially overhead bolted structures)',
        hasAnomalies: '',
        anomalyItems: [],
        generalPhotos: [],
    },
    {
        label: 'Ensure all fastenings, bolts, clips, covers, panels, hatches, and removable guardrails are properly secured.',
        hasAnomalies: '',
        anomalyItems: [],
        generalPhotos: [],
    },
    {
        label: 'Check that all safety securing devices are properly installed and secured to a sound structure.',
        hasAnomalies: '',
        anomalyItems: [],
        generalPhotos: [],
    },
    {
        label: 'For any overhead platform, monkey ladders, attachments, check for signs of corrosion or paint peel-off',
        hasAnomalies: '',
        anomalyItems: [],
        generalPhotos: [],
    },
    {
        label: 'Ensure all guards, barricades, and toe boards are in place. Close or cover any hatches, openings, and gaps.',
        hasAnomalies: '',
        anomalyItems: [],
        generalPhotos: [],
    },
    {
        label: 'Identify lighting, cable trays, brackets, and other fixtures. Pay special attention to any corroded items that may not be secure or could present a snagging or collision hazard.',
        hasAnomalies: '',
        anomalyItems: [],
        generalPhotos: [],
    },
    {
        label: 'Grating alignment shall be within 1inch to prevent trip hazard.',
        hasAnomalies: '',
        anomalyItems: [],
        generalPhotos: [],
    },
];

export default CreatePDOReport;